class TgProductAddOnCart {
  constructor(form) {
    this.form = form;
    this.productForm = form.closest('product-form');
    this.addOns = [...document.querySelectorAll('[data-tg-addon]')];
    this.onSubmit = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.onSubmit, true);
  }

  selectedAddOns() {
    return this.addOns.filter((addOn) => addOn.querySelector('[data-tg-addon-toggle]')?.checked);
  }

  async onSubmit(event) {
    if (event.submitter?.closest('.shopify-payment-button')) return;

    const addOns = this.selectedAddOns();
    if (!addOns.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitButton = this.form.querySelector('[type="submit"]');
    const cart = document.querySelector('cart-notification, cart-drawer');
    const formData = new FormData(this.form);
    const properties = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)]$/);
      if (match && value) properties[match[1]] = value;
    });

    const items = [
      {
        id: formData.get('id'),
        quantity: Number(formData.get('quantity')) || 1,
        properties,
      },
      ...addOns.map((addOn) => ({ id: addOn.dataset.variantId, quantity: 1 })),
    ];

    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');
    this.productForm?.handleErrorMessage?.();

    try {
      const payload = { items, sections_url: window.location.pathname };
      if (cart) payload.sections = cart.getSectionsToRender().map((section) => section.id);

      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || addOns[0].dataset.errorMessage);

      if (cart) {
        cart.setActiveElement?.(document.activeElement);
        cart.renderContents(data);
      } else {
        window.location.assign(routes.cart_url);
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tg-addon', cartData: data });
      }
    } catch (error) {
      this.productForm?.handleErrorMessage?.(error.message || 'Unable to add items to cart.');
    } finally {
      submitButton?.classList.remove('loading');
      submitButton?.removeAttribute('aria-disabled');
    }
  }
}

function bindTgProductAddOnForms(container = document) {
  container.querySelectorAll('product-form form').forEach((form) => {
    if (form.closest('product-form')?.dataset.tgAddonBound) return;
    form.closest('product-form').dataset.tgAddonBound = 'true';
    new TgProductAddOnCart(form);
  });
}

// Whether TeeInBlue has published a campaign for this product. The campaign is
// the plugin's own verdict on what the product is, so it also corrects a stale
// `teeinblue.campaign_version` metafield that main-product.liquid matched on.
// Returns undefined while the plugin has not reported yet, which is exactly the
// window the guard below exists to cover — callers must not read that as "no".
// TeeInBlue publishes nothing for products it does not personalize, so for those
// the "no" comes from tg-customization-bootstrap finding no inline campaign.
function tgGetTeeInBlueVerdict(productRoot) {
  const campaign = window.teeinblueCampaign;
  if (!campaign) {
    const lifecycle = window.TgCustomizationLifecycle;
    const ownLifecycle = lifecycle && lifecycle.productId === productRoot?.dataset.productId;
    return ownLifecycle && lifecycle.tee === 'absent' ? false : undefined;
  }
  if (campaign.isTeeInBlueProduct === undefined) return undefined;
  // A campaign for some other product says nothing about this one, so it stays
  // undecided rather than counting as "needs no customization".
  if (campaign.productId && productRoot?.dataset.productId) {
    if (String(campaign.productId) !== productRoot.dataset.productId) return undefined;
  }
  return campaign.isTeeInBlueProduct === true;
}

// TeeInBlue is ready once the form it adds to cart through is actually mounted.
function tgGetTeeInBlueForm(productRoot) {
  const customizationForm = productRoot?.querySelector('#tee-artwork-form');
  const pluginButton = customizationForm?.querySelector('#teeAtcButton');
  if (!customizationForm || !pluginButton) return null;
  return { customizationForm, pluginButton };
}

// TeeInBlue mounts its customization controls outside Shopify's product form.
// Its own add-to-cart action creates the customization and submits the line
// item with the generated properties. When the plugin action area is visually
// hidden, route the visible theme button through that flow instead of
// submitting the base variant without its customization metadata.
class TgTeeInBlueCartBridge {
  constructor() {
    this.pendingForm = null;
    this.pendingButton = null;
    this.customizationStarted = false;
    this.validationTimer = null;
    this.fallbackTimer = null;

    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onCustomizationStarted = this.onCustomizationStarted.bind(this);
    this.reset = this.reset.bind(this);

    document.addEventListener('click', this.onClick, true);
    document.addEventListener('submit', this.onSubmit, true);
    document.addEventListener('teeinblue-event-before-customization-created', this.onCustomizationStarted);
    document.addEventListener('teeinblue-event-after-cart-added', this.reset);
    document.addEventListener('teeinblue-event-error', this.reset);
  }

  getContext(event) {
    let form;
    let button;

    if (event.type === 'submit') {
      form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[data-type="add-to-cart-form"]')) return null;
      if (event.submitter?.closest?.('.shopify-payment-button')) return null;
      button = event.submitter?.matches?.('button.product-form__submit[type="submit"]')
        ? event.submitter
        : form.querySelector('button.product-form__submit[type="submit"]');
    } else {
      button = event.target.closest?.('button.product-form__submit[type="submit"]');
      form = button?.form;
    }

    const productRoot = form?.closest('product-info[data-product-id]');
    if (!button || !productRoot || !productRoot.contains(button)) return null;
    if (tgGetTeeInBlueVerdict(productRoot) !== true) return null;

    const pluginForm = tgGetTeeInBlueForm(productRoot);
    if (!pluginForm) return null;

    return { form, button, ...pluginForm };
  }

  preventThemeSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  syncQuantity(form, customizationForm) {
    const themeQuantity = Number(new FormData(form).get('quantity')) || 1;
    const pluginQuantity = customizationForm.querySelector('.tee-quantity-input');
    if (!pluginQuantity || Number(pluginQuantity.value) === themeQuantity) return;

    pluginQuantity.value = String(themeQuantity);
    pluginQuantity.dispatchEvent(new Event('input', { bubbles: true }));
    pluginQuantity.dispatchEvent(new Event('change', { bubbles: true }));
  }

  setButtonLoading(button, loading) {
    if (!button) return;

    if (loading) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      button.dataset.tgTeeinbluePending = 'true';
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      return;
    }

    if (button.dataset.tgTeeinbluePending !== 'true') return;
    delete button.dataset.tgTeeinbluePending;
    button.removeAttribute('aria-disabled');
    button.classList.remove('loading');
    button.querySelector('.loading__spinner')?.classList.add('hidden');
  }

  begin(context, event) {
    this.preventThemeSubmit(event);
    this.pendingForm = context.form;
    this.pendingButton = context.button;
    this.customizationStarted = false;
    this.setButtonLoading(context.button, true);

    window.clearTimeout(this.validationTimer);
    window.clearTimeout(this.fallbackTimer);
    this.fallbackTimer = window.setTimeout(this.reset, 60_000);

    try {
      this.syncQuantity(context.form, context.customizationForm);
      context.pluginButton.click();

      // Validation failures do not consistently emit a public TeeInBlue error
      // event. A valid submission emits BEFORE_CUSTOMIZATION_CREATED first.
      this.validationTimer = window.setTimeout(() => {
        if (this.pendingForm === context.form && !this.customizationStarted) this.reset();
      }, 500);
    } catch (error) {
      console.error('[TeeInBlue] Unable to start customization-aware add to cart', error);
      this.reset();
    }
  }

  // A null context means either "not a TeeInBlue product" or "TeeInBlue has not
  // loaded yet". The second case must not reach the theme's submit, or it adds
  // the base variant with no customization properties — but tg-customization-
  // bootstrap already blocks every unready product in the capture phase, before
  // this listener runs. Here a null context only ever means the first case.
  onClick(event) {
    const context = this.getContext(event);
    if (!context) return;

    if (this.pendingForm) {
      this.preventThemeSubmit(event);
      return;
    }

    if (context.button.disabled || context.button.getAttribute('aria-disabled') === 'true') return;
    this.begin(context, event);
  }

  onSubmit(event) {
    const context = this.getContext(event);
    if (!context) return;

    if (this.pendingForm) {
      this.preventThemeSubmit(event);
      return;
    }

    this.begin(context, event);
  }

  onCustomizationStarted() {
    if (!this.pendingForm) return;
    this.customizationStarted = true;
    window.clearTimeout(this.validationTimer);
  }

  reset() {
    this.setButtonLoading(this.pendingButton, false);
    this.pendingForm = null;
    this.pendingButton = null;
    this.customizationStarted = false;
    window.clearTimeout(this.validationTimer);
    window.clearTimeout(this.fallbackTimer);
  }
}

// All products start locked in Liquid. Each customized product uses one plugin;
// once identified, only that plugin's readiness controls the purchase buttons.
//
// Failing to load keeps the buttons disabled rather than opening that window on
// a timer: an order missing its customization cannot be fulfilled as placed, so
// it costs more than the lost sale.
const TG_CUSTOMIZATION_POLL_INTERVAL = 200;

class TgCustomizationGuard {
  constructor(root) {
    this.root = root;
    this.loadingText = root.dataset.tgCustomizationLoading || '';
    this.errorText = root.dataset.tgCustomizationError || '';
    this.state = 'pending';
    this.stockDisabled = Boolean(this.submitButton?.hasAttribute('data-tg-stock-disabled'));
    this.variantPending = false;
    this.check = this.check.bind(this);
    this.observer = new MutationObserver(this.check);
    this.observe();
    this.pollTimer = window.setInterval(this.check, TG_CUSTOMIZATION_POLL_INTERVAL);
    document.addEventListener('tg:customization-state', this.check);
    this.armDeadline();
    this.check();
  }

  get lifecycle() {
    const state = window.TgCustomizationLifecycle;
    return state?.productId === this.root.dataset.productId ? state : null;
  }

  get submitButton() {
    return this.root.querySelector('button.product-form__submit[type="submit"]');
  }

  get buttons() {
    return this.root.querySelectorAll('.product-form__submit, #customily-cart-btn, #customily-buy-now-btn');
  }

  get customized() {
    return tgGetTeeInBlueVerdict(this.root) === true || this.lifecycle?.customily === 'required';
  }

  isReady() {
    const state = this.lifecycle;
    const teeVerdict = tgGetTeeInBlueVerdict(this.root);
    if (!state) return false;
    if (teeVerdict === true) {
      const form = tgGetTeeInBlueForm(this.root);
      return Boolean(state.teeMounted && form && !form.pluginButton.disabled && !form.pluginButton.classList.contains('tee-processing'));
    }
    if (state.customily === 'required') {
      return state.optionsReady && (!state.loaders || state.cartPending) && !state.preview && !state.customization &&
        Boolean(this.root.querySelector('#customily-cart-btn, #customily-personalize-button'));
    }
    // A negative verdict alone cannot establish that this is a plain product.
    return teeVerdict === false && state.customily === 'absent';
  }

  observe() {
    this.observer.observe(this.root, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class']
    });
  }

  armDeadline() {
    window.clearTimeout(this.deadlineTimer);
    this.deadlineTimer = window.setTimeout(() => {
      this.state = 'failed';
      this.apply();
    }, (Number(this.root.dataset.tgCustomizationTimeout) || 20) * 1000);
  }

  check() {
    if (!this.root.isConnected) return this.destroy();
    const ready = this.isReady();
    if (ready && this.state !== 'ready') {
      this.state = 'ready';
      window.clearTimeout(this.deadlineTimer);
      // The poll only covers a verdict that lands on a global
      // (window.teeinblueCampaign) with neither an event nor a DOM change, which
      // can only happen before the first ready. Every later transition arrives
      // through the observer or tg:customization-state.
      window.clearInterval(this.pollTimer);
      const wrapper = this.root.querySelector('.product-form__error-message-wrapper');
      if (wrapper?.dataset.tgGuardError === 'true') {
        wrapper.hidden = true;
        delete wrapper.dataset.tgGuardError;
      }
    } else if (!ready && this.state === 'ready') {
      this.state = 'pending';
      this.armDeadline();
    }
    this.apply();
  }

  // Called synchronously by the theme, including the unavailable-variant path
  // that does not publish variantChange.
  setVariantState(disabled, pending = false) {
    this.stockDisabled = disabled;
    this.variantPending = pending;
    this.check();
  }

  apply() {
    // Disconnect during our own writes to avoid a MutationObserver feedback loop.
    this.observer.disconnect();
    const blocked = this.state !== 'ready' || this.stockDisabled || this.variantPending;
    this.root.dataset.tgCustomizationGuard = blocked ? (this.state === 'failed' ? 'failed' : 'pending') : 'ready';
    this.root.querySelectorAll('[data-tg-customization-guard-hidden]').forEach((wrapper) => {
      wrapper.hidden = blocked || this.customized;
    });
    this.buttons.forEach((button) => {
      if (blocked) {
        button.dataset.tgGuardLocked = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        const loading = !this.stockDisabled && this.state === 'pending';
        button.classList.toggle('loading', loading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (loading) this.setLabel(button, this.loadingText);
        // Mirror the theme button's sold-out/unavailable text onto the plugin's
        // cloned buttons, which keep whatever label they were cloned with.
        else if (this.stockDisabled) this.setLabel(button, this.submitButton?.querySelector('span')?.textContent);
      } else if (button.dataset.tgGuardLocked === 'true' || button.dataset.tgCustomizationGuard === 'pending') {
        // Release only a lock we own. Never clear an in-flight cart operation.
        if (button.dataset.tgTeeinbluePending !== 'true' && !this.root.querySelector('product-form')?.tgSubmitting) {
          delete button.dataset.tgGuardLocked;
          button.dataset.tgCustomizationGuard = 'ready';
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
          this.setLabel(button, window.variantStrings?.addToCart);
        }
      }
    });
    if (this.state === 'failed' && !this.stockDisabled) this.showError();
    this.observe();
  }

  setLabel(button, text) {
    const label = button.querySelector('span');
    if (label && text && label.textContent !== text) label.textContent = text;
  }

  showError() {
    const wrapper = this.root.querySelector('.product-form__error-message-wrapper');
    const message = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !message || !this.errorText) return;
    message.textContent = this.errorText;
    wrapper.dataset.tgGuardError = 'true';
    wrapper.hidden = false;
  }

  destroy() {
    this.observer.disconnect();
    window.clearInterval(this.pollTimer);
    window.clearTimeout(this.deadlineTimer);
    document.removeEventListener('tg:customization-state', this.check);
    delete this.root.tgCustomizationGuard;
  }
}

function bindTgCustomizationGuards(container = document) {
  const roots = [];
  if (container.matches?.('product-info[data-tg-requires-customization]')) roots.push(container);
  roots.push(...container.querySelectorAll('product-info[data-tg-requires-customization]'));

  roots.forEach((root) => {
    if (root.tgCustomizationGuard) return;
    root.tgCustomizationGuard = new TgCustomizationGuard(root);
  });
}

// Customily renders its live preview onto a canvas mounted inside one of the
// product media slides, then relies on that slide being the one the shopper
// sees. That holds for Dawn's stacked desktop gallery, but this theme turns the
// desktop gallery into a horizontal carousel (see tg-product-page.css), so the
// preview can end up scrolled off-screen and selecting an option appears to do
// nothing. Customily's options live outside <variant-selects>, so none of the
// theme's variant plumbing runs either — bridge the two here by scrolling the
// carousel to whichever slide currently hosts the canvas.
const TG_CUSTOMILY_CANVAS_SELECTOR = '.cl-canvas-container';
const TG_CUSTOMILY_OPTION_SELECTOR = '.customily_option, .customily-swatch, .cl-option-content';
const TG_GALLERY_NAV_SELECTOR = '.slider-button, .thumbnail-list__item button, .tg-gallery-dot';
const TG_CUSTOMILY_RESPONSE_WINDOW = 2000;
const TG_CUSTOMILY_DEBOUNCE_DELAY = 150;
const TG_CUSTOMILY_POSITION_EPSILON = 4;

const tgCustomilyPreviewSyncers = [];
let tgCustomilyLastInteraction = 0;

class TgCustomilyPreviewSync {
  constructor(gallery) {
    this.gallery = gallery;
    this.timer = null;
    this.syncToPreview = this.syncToPreview.bind(this);

    // The app re-mounts the canvas several times per update while it lays out
    // responsively, so debounce instead of reacting to every mutation.
    new MutationObserver(() => this.schedule()).observe(gallery, { childList: true, subtree: true });
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(this.syncToPreview, TG_CUSTOMILY_DEBOUNCE_DELAY);
  }

  cancel() {
    clearTimeout(this.timer);
  }

  syncToPreview() {
    // Only follow the canvas just after an option was used. Otherwise the app's
    // own responsive re-layouts would yank the carousel away from an image the
    // shopper deliberately navigated to.
    if (performance.now() - tgCustomilyLastInteraction > TG_CUSTOMILY_RESPONSE_WINDOW) return;

    const canvas = this.gallery.querySelector(TG_CUSTOMILY_CANVAS_SELECTOR);
    const slide = canvas?.closest('li[data-media-id]');
    if (!slide) return;

    // The canvas is already on screen. Moving again would fight the shopper, and
    // setActiveMedia also nudges the window's scroll position.
    const slider = slide.parentElement;
    if (Math.abs(slider.scrollLeft - slide.offsetLeft) < TG_CUSTOMILY_POSITION_EPSILON) return;

    // prepend stays false on purpose: reordering the slides would move the first
    // media item, which is what Customily mounts its canvas against.
    this.gallery.setActiveMedia?.(slide.dataset.mediaId, false);
  }
}

// Manual gallery navigation wins over the preview. Closing the follow window
// stops a queued sync from dragging the carousel back to the canvas slide after
// the shopper picked a thumbnail or arrow.
function releaseTgCustomilyFollow(event) {
  if (!event.target.closest?.(TG_GALLERY_NAV_SELECTOR)) return;

  tgCustomilyLastInteraction = 0;
  tgCustomilyPreviewSyncers.forEach((syncer) => syncer.cancel());
}

function trackTgCustomilyInteraction(event) {
  if (!event.target.closest?.(TG_CUSTOMILY_OPTION_SELECTOR)) return;

  tgCustomilyLastInteraction = performance.now();
  // Re-bind first: a product swap replaces the gallery via viewTransition
  // without firing shopify:section:load, so the fresh one is still unbound.
  bindTgCustomilyPreviewSync();
  // Sync even if the app updates the canvas without touching the DOM.
  tgCustomilyPreviewSyncers.forEach((syncer) => syncer.schedule());
}

function bindTgCustomilyPreviewSync(container = document) {
  for (let i = tgCustomilyPreviewSyncers.length - 1; i >= 0; i--) {
    if (!tgCustomilyPreviewSyncers[i].gallery.isConnected) tgCustomilyPreviewSyncers.splice(i, 1);
  }

  container.querySelectorAll('media-gallery').forEach((gallery) => {
    if (gallery.dataset.tgCustomilySync) return;
    gallery.dataset.tgCustomilySync = 'true';
    tgCustomilyPreviewSyncers.push(new TgCustomilyPreviewSync(gallery));
  });
}

const TG_SIZE_CHART_NATIVE_GROUP_SELECTOR = 'variant-selects .product-form__input';
const TG_SIZE_CHART_TEEINBLUE_GROUP_SELECTOR = '#tee-artwork-form .tee-option';

class TgSizeChart {
  constructor(root) {
    this.root = root;
    this.modalId = root.dataset.tgSizeChartModalId;
    this.label = root.dataset.tgSizeChartLabel || 'Size Chart';
    this.optionNames = new Set(
      (root.dataset.tgSizeChartOptionNames || 'Size,尺码,尺寸')
        .split(/[,，]/)
        .map((name) => this.normalize(name))
        .filter(Boolean)
    );
    this.animationFrame = null;
    this.scheduleSync = this.scheduleSync.bind(this);
    this.observer = new MutationObserver(this.scheduleSync);
    this.observer.observe(root, { childList: true, subtree: true, characterData: true });
    this.sync();
  }

  normalize(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  getNativeOptionName(group) {
    const namedControl = group.querySelector('[data-option-name]');
    if (namedControl?.dataset.optionName) return namedControl.dataset.optionName;

    const selectName = group.querySelector('select[name^="options["]')?.getAttribute('name') || '';
    return selectName.match(/^options\[(.*)]$/)?.[1] || '';
  }

  getTeeInBlueOptionName(group) {
    return group.querySelector('.tee-option__title')?.textContent || '';
  }

  createButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tg-size-chart__button link';
    button.dataset.tgSizeChartOpener = 'true';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-controls', this.modalId);
    button.textContent = this.label;
    button.addEventListener('click', () => {
      const modal = document.getElementById(this.modalId);
      if (typeof modal?.show === 'function') modal.show(button);
    });
    return button;
  }

  syncGroup(group, optionName, title) {
    const existingButton = group.querySelector('[data-tg-size-chart-opener]');
    const isSizeOption = this.optionNames.has(this.normalize(optionName));

    if (!isSizeOption || !title) {
      existingButton?.remove();
      group.classList.remove('tg-size-chart-option');
      return;
    }

    group.classList.add('tg-size-chart-option');
    const button = existingButton || this.createButton();
    if (title.tagName === 'LEGEND') {
      if (button.parentElement !== title) title.appendChild(button);
    } else if (title.nextElementSibling !== button) {
      title.insertAdjacentElement('afterend', button);
    }
  }

  sync() {
    this.root.querySelectorAll(TG_SIZE_CHART_NATIVE_GROUP_SELECTOR).forEach((group) => {
      this.syncGroup(group, this.getNativeOptionName(group), group.querySelector('.form__label'));
    });

    this.root.querySelectorAll(TG_SIZE_CHART_TEEINBLUE_GROUP_SELECTOR).forEach((group) => {
      this.syncGroup(group, this.getTeeInBlueOptionName(group), group.querySelector('.tee-option__title'));
    });
  }

  scheduleSync() {
    if (this.animationFrame) return;
    this.animationFrame = window.requestAnimationFrame(() => {
      this.animationFrame = null;
      this.sync();
    });
  }

  destroy() {
    this.observer.disconnect();
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
    document.getElementById(this.modalId)?.remove();
  }
}

function bindTgSizeCharts(container = document) {
  const roots = [];
  if (container.matches?.('product-info[data-tg-size-chart]')) roots.push(container);
  roots.push(...container.querySelectorAll('product-info[data-tg-size-chart]'));

  roots.forEach((root) => {
    if (root.tgSizeChart) return;
    root.tgSizeChart = new TgSizeChart(root);
  });
}

// TeeInBlue and Customily inject their option groups client-side, so the
// single-value check in product-variant-picker.liquid never sees them. Mirror
// that rule here: once a group is down to one visible value there is nothing
// left to pick, so hide the group instead of leaving a dead row on the page.
const TG_SINGLE_VALUE_GROUP_SELECTOR = '#tee-artwork-form .tee-option, .customily_option';
const TG_SINGLE_VALUE_CLASS = 'tg-single-value-option';

// Ordered from plugin-specific to generic: the first selector that matches
// anything defines what counts as a value for that group, so a plugin's own
// markup wins over the fallbacks.
const TG_SINGLE_VALUE_CANDIDATE_SELECTORS = [
  '.tee-radio',
  '.cl-swatch',
  '.customily-swatch',
  '[role="radio"]',
  'input[type="radio"]',
];

class TgSingleValueOptions {
  constructor(root) {
    this.root = root;
    this.animationFrame = null;
    this.scheduleSync = this.scheduleSync.bind(this);

    // Availability usually flips through a class or an inline style rather than
    // a node insertion, so watch attributes as well and let the frame throttle
    // absorb the churn.
    this.observer = new MutationObserver(this.scheduleSync);
    this.observe();
    this.sync();
  }

  observe() {
    this.observer.observe(this.root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  }

  isVisible(element) {
    if (element.hidden) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;

    const bounds = element.getBoundingClientRect();
    return bounds.width > 1 && bounds.height > 1;
  }

  countValues(group) {
    // A lone checkbox is an opt-in (gift wrap, add-on), not a value to pick:
    // ticking it for the shopper would force a choice they may not want. Keep
    // checkbox groups out entirely, even when styled as swatches.
    if (group.querySelector('input[type="checkbox"]')) return 0;

    // A select keeps its values in option elements, which have no box to
    // measure. Customily prefixes its dropdowns with an empty placeholder
    // ("Choose an Option") that is not a pickable value.
    const select = group.querySelector('select');
    if (select) return [...select.options].filter((option) => option.value !== '').length;

    for (const selector of TG_SINGLE_VALUE_CANDIDATE_SELECTORS) {
      const candidates = [...group.querySelectorAll(selector)];
      if (candidates.length) return candidates.filter((candidate) => this.isVisible(candidate)).length;
    }

    // Upload, text and date fields hold no values at all. Reporting zero keeps
    // them out of the rule instead of hiding the control the shopper needs.
    return 0;
  }

  // Hiding a required group with nothing selected would strand the shopper: the
  // plugin refuses to add to cart and the offending field is off screen. Both
  // branches are idempotent, so a re-sync after the plugin repaints is a no-op.
  // Returns whether the plugin was handed an event it may repaint against.
  selectOnlyValue(group) {
    const select = group.querySelector('select');
    if (select) {
      const onlyOption = [...select.options].find((option) => option.value !== '');
      if (!onlyOption || select.value === onlyOption.value) return false;

      select.value = onlyOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const input = group.querySelector('input[type="radio"]');
    if (!input || input.checked) return false;

    input.click();
    return true;
  }

  sync() {
    let selectionChanged = false;

    this.root.querySelectorAll(TG_SINGLE_VALUE_GROUP_SELECTOR).forEach((group) => {
      // Measure with our own class off: while it is on, display:none collapses
      // every child's box, the group reads as valueless and it would flip back
      // to visible. Removing and re-adding within one task never paints.
      group.classList.remove(TG_SINGLE_VALUE_CLASS);

      // The plugin already hides groups whose conditions are unmet. Leave those
      // alone rather than selecting values inside a field nobody can see.
      if (!this.isVisible(group)) return;
      if (this.countValues(group) !== 1) return;

      selectionChanged = this.selectOnlyValue(group) || selectionChanged;
      group.classList.add(TG_SINGLE_VALUE_CLASS);
    });

    // Drop the records this pass just generated so toggling our own class does
    // not schedule the next pass forever.
    this.observer.takeRecords();

    // Selecting a value runs the plugin's own handler synchronously, and the
    // records for whatever it rebuilt were just discarded. Re-evaluate once so
    // that repaint is not missed; the idempotent branches above end the chain.
    if (selectionChanged) this.scheduleSync();
  }

  scheduleSync() {
    if (this.animationFrame) return;
    this.animationFrame = window.requestAnimationFrame(() => {
      this.animationFrame = null;
      this.sync();
    });
  }

  destroy() {
    this.observer.disconnect();
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
    this.root.querySelectorAll(`.${TG_SINGLE_VALUE_CLASS}`).forEach((group) => {
      group.classList.remove(TG_SINGLE_VALUE_CLASS);
    });
  }
}

function bindTgSingleValueOptions(container = document) {
  const roots = [];
  if (container.matches?.('product-info[data-tg-hide-single-options]')) roots.push(container);
  roots.push(...container.querySelectorAll('product-info[data-tg-hide-single-options]'));

  roots.forEach((root) => {
    if (root.tgSingleValueOptions) return;
    root.tgSingleValueOptions = new TgSingleValueOptions(root);
  });
}

document.addEventListener('click', releaseTgCustomilyFollow, true);
document.addEventListener('click', trackTgCustomilyInteraction, true);
document.addEventListener('change', trackTgCustomilyInteraction, true);

document.addEventListener('DOMContentLoaded', () => {
  bindTgProductAddOnForms();
  bindTgCustomilyPreviewSync();
  bindTgSizeCharts();
  bindTgSingleValueOptions();
  bindTgCustomizationGuards();
  window.TgTeeInBlueCartBridge ||= new TgTeeInBlueCartBridge();
});

document.addEventListener('shopify:section:load', (event) => {
  bindTgProductAddOnForms(event.target);
  bindTgCustomilyPreviewSync(event.target);
  bindTgSizeCharts(event.target);
  bindTgSingleValueOptions(event.target);
  bindTgCustomizationGuards(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('product-info[data-tg-size-chart]').forEach((root) => {
    root.tgSizeChart?.destroy();
  });
  event.target.querySelectorAll('product-info[data-tg-hide-single-options]').forEach((root) => {
    root.tgSingleValueOptions?.destroy();
  });
  event.target.querySelectorAll('product-info[data-tg-requires-customization]').forEach((root) => {
    root.tgCustomizationGuard?.destroy();
  });
});
